import PropTypes from 'prop-types';
import {forwardRef} from 'react';
// @mui
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
// routes
import {RouterLink} from 'packages/common/routes/components';

// ----------------------------------------------------------------------
/**
 * Logo component displays the logo of the application.
 * The logo can either be a small logo or a full logo, and can optionally be wrapped in a link.
 *
 * @component
 * @example
 * <Logo disabledLink={true} isSmall={true} sx={{marginTop: '10px'}} />
 * <Logo variant="white" isSmall={true} />
 *
 * @param {object} props - Props for the component.
 * @param {boolean} [props.disabledLink=false] - Determines if the logo should be wrapped in a link or not.
 * @param {object} [props.sx] - Additional styles to be applied to the logo.
 * @param {boolean} [props.isSmall=false] - Determines if the logo to be shown should be small or full.
 * @param {string} [props.variant='default'] - Logo variant: 'default' or 'white' for dark backgrounds.
 * @param {Ref} ref - React ref to forward.
 *
 * @returns {JSX.Element}
 */
const Logo = forwardRef(({disabledLink = false, sx, isSmall = false, variant = 'default', ...other}, ref) => {
  // Local logo (public folder)
  // -------------------------------------------------------
  const logoSrc = variant === 'white' ?
    (isSmall ? '/logo/logo_white.svg' : '/logo/logo_full_white.svg') :
    (isSmall ? '/logo/logo.svg' : '/logo/logo_full.svg');

  const logo = (
    <Box
      component="img"
      src={logoSrc}
      sx={{
        width: isSmall ? 40 : 140,
        height: isSmall ? 40 : 64,
        cursor: 'pointer',
        ...sx,
      }}
    />
  );

  if (disabledLink) {
    return logo;
  }

  return (
    <Link component={RouterLink} href="/" sx={{display: 'contents'}}>
      {logo}
    </Link>
  );
});

Logo.displayName = 'Logo';

Logo.propTypes = {
  disabledLink: PropTypes.bool,
  sx: PropTypes.object,
  isSmall: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'white']),
};

export default Logo;
